import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, MessageSquare } from 'lucide-react';
import { messageTemplateService, integrationService, sessionService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { MessageTemplate, MessageCategory } from '../types/messageTemplate';
import type { Integration, IntegrationResponse } from '../types/integration';
import type { WhatsAppSession } from '../types/assistant';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import TemplateList from '../components/messages/TemplateList';
import TemplateForm from '../components/messages/TemplateForm';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function MessageTemplates() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>('');
  const queryClient = useQueryClient();

  // Queries
  const { data: userTemplates, isLoading: isLoadingUserTemplates } = useQuery({
    queryKey: ['user-templates'],
    queryFn: () => messageTemplateService.getUserTemplates(),
  });

  const { data: categories } = useQuery({
    queryKey: ['message-categories'],
    queryFn: () => messageTemplateService.getCategories(),
  });

  const { data: integrationsData } = useQuery({
    queryKey: ['integrations-list'],
    queryFn: () => integrationService.getIntegrations(),
  });

  const { data: whatsappSessions } = useQuery({
    queryKey: ['whatsapp-sessions'],
    queryFn: () => sessionService.getSessions(),
  });

  const integrations: Integration[] =
    (integrationsData as IntegrationResponse)?.data ||
    (Array.isArray(integrationsData) ? integrationsData : []);
  const safeWhatsappSessions: WhatsAppSession[] =
    Array.isArray(whatsappSessions) ? whatsappSessions : [];

  // Query for default template based on selected category
  const { data: defaultTemplate, refetch: refetchDefaultTemplate } = useQuery({
    queryKey: ['default-template', selectedCategoryKey],
    queryFn: () => messageTemplateService.getTemplateByCategory(selectedCategoryKey),
    enabled: !!selectedCategoryKey && selectedCategoryKey !== '',
    onError: () => {
      // Silently fail if no default template exists
    },
  });

  // Mutations for User Templates
  const createUserTemplateMutation = useMutation({
    mutationFn: (data: Partial<MessageTemplate>) => messageTemplateService.createUserTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-templates'] });
      messageHandler.success('Modelo de mensagem criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateUserTemplateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<MessageTemplate> }) =>
      messageTemplateService.updateUserTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-templates'] });
      messageHandler.success('Modelo de mensagem atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteUserTemplateMutation = useMutation({
    mutationFn: (id: number) => messageTemplateService.deleteUserTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-templates'] });
      messageHandler.success('Modelo de mensagem excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const messageJsonStr = formData.get('message_json') as string;
    let messageJson = null;

    if (messageJsonStr && messageJsonStr.trim()) {
      try {
        messageJson = JSON.parse(messageJsonStr);
      } catch (error) {
        messageHandler.error('JSON inválido no campo Dados JSON');
        return;
      }
    }

    const data = {
      name: formData.get('name') as string || null,
      category_key: formData.get('category_key') as string,
      message_template: formData.get('message_template') as string,
      message_json: messageJson,
      integration_id: formData.get('integration_id')
        ? Number(formData.get('integration_id'))
        : null,
      integration_key: formData.get('integration_key')
        ? (formData.get('integration_key') as string)
        : null,
      whatsapp_id: formData.get('whatsapp_id')
        ? Number(formData.get('whatsapp_id'))
        : null,
      hsm_key: formData.get('hsm_key')
        ? (formData.get('hsm_key') as string)
        : null,
    };

    if (editingTemplate) {
      updateUserTemplateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createUserTemplateMutation.mutate(data);
    }
  };

  const handleDelete = (template: MessageTemplate) => {
    messageHandler.confirm(
      'Excluir Modelo',
      'Tem certeza que deseja excluir este modelo de mensagem?',
      () => {
        deleteUserTemplateMutation.mutate(template.id);
      }
    );
  };

  const handleCategoryChange = (categoryKey: string) => {
    setSelectedCategoryKey(categoryKey);
  };

  const handleLoadDefault = async () => {
    if (!selectedCategoryKey) {
      messageHandler.warning('Por favor, selecione uma categoria primeiro.');
      return;
    }

    try {
      // Refetch to ensure we have the latest data
      await refetchDefaultTemplate();

      if (defaultTemplate) {
        const textareaElement = document.getElementById('message_template') as HTMLTextAreaElement;
        if (textareaElement) {
          textareaElement.value = defaultTemplate.message_template;
        }

        const jsonTextareaElement = document.getElementById('message_json') as HTMLTextAreaElement;
        if (jsonTextareaElement && defaultTemplate.message_json) {
          jsonTextareaElement.value = JSON.stringify(defaultTemplate.message_json, null, 2);
        }

        messageHandler.success('Modelo padrão carregado com sucesso!');
      } else {
        messageHandler.warning('Não foi encontrado um modelo padrão para esta categoria.');
      }
    } catch (error) {
      messageHandler.error('Não foi possível carregar o modelo padrão.');
    }
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingTemplate(null);
    setSelectedCategoryKey('');
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Modelos de Mensagens"
          description="Gerencie seus modelos de mensagens personalizados."
          actions={
            <Button
              onClick={() => {
                setEditingTemplate(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Modelo
            </Button>
          }
        />

        <div className="mt-4">
          <TemplateList
            templates={userTemplates || []}
            onEdit={template => {
              setEditingTemplate(template);
              setSelectedCategoryKey(template.category_key);
              setModal({ type: 'edit' });
            }}
            onDelete={handleDelete}
            isLoading={isLoadingUserTemplates}
          />
        </div>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingTemplate ? 'Editar Modelo de Mensagem' : 'Novo Modelo de Mensagem'}
          size="xl"
        >
          <TemplateForm
            template={editingTemplate}
            categories={categories || []}
            integrations={integrations}
            whatsappSessions={safeWhatsappSessions}
            isGlobal={false}
            onSubmit={handleSubmit}
            onLoadDefault={handleLoadDefault}
            onCategoryChange={handleCategoryChange}
            isLoading={createUserTemplateMutation.isPending || updateUserTemplateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default MessageTemplates;
